import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Cpu, HardDrive, Wifi, WifiOff, Plus, Trash2, RefreshCw,
  Server, Cloud, Box, Network, Clock, Activity, Info, Copy,
  CheckCircle2, ChevronDown, ChevronUp, Terminal, BookOpen,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';

import { API } from '../../lib/api';

const agentTypeIcons = {
  kubernetes: Box,
  vm: Server,
  cloud: Cloud,
  network: Network,
};

const statusColors = {
  active: { bg: 'bg-green-500/15', text: 'text-green-500', dot: 'bg-green-500' },
  inactive: { bg: 'bg-yellow-500/15', text: 'text-yellow-500', dot: 'bg-yellow-500' },
  pending: { bg: 'bg-blue-500/15', text: 'text-blue-500', dot: 'bg-blue-500' },
  error: { bg: 'bg-red-500/15', text: 'text-red-500', dot: 'bg-red-500' },
};

// Per-type descriptions shown in the Register dialog when each type is selected.
const AGENT_TYPE_INFO = {
  kubernetes: {
    title: 'Kubernetes Agent',
    description: 'Runs as a DaemonSet on every node of a cluster. Collects pod metrics, container logs, k8s events and node-level system metrics.',
    install: 'Helm chart on the cluster (see install steps after registration).',
    examples: 'EKS, GKE, AKS, on-prem k8s, OpenShift',
  },
  vm: {
    title: 'VM / EC2 Agent',
    description: 'A systemd service that runs on a single VM, EC2 instance, or bare-metal host. Collects host metrics (CPU, memory, disk, network) and tails configured log files.',
    install: 'systemd unit installed via curl + bash one-liner.',
    examples: 'EC2, Azure VMs, on-prem servers, lightsail',
  },
  cloud: {
    title: 'Cloud API Agent',
    description: 'A serverless collector that pulls data via the cloud provider\'s management API — CloudWatch metrics, billing data, AWS Config snapshots, etc. Does NOT run inside your workloads.',
    install: 'Lambda / Cloud Function deployed via Terraform module.',
    examples: 'CloudWatch scraper, GCP Stackdriver scraper, Azure Monitor pull',
  },
  network: {
    title: 'Network Agent',
    description: 'Captures VPC flow logs, NAT/firewall logs and routing telemetry. Used for connectivity, latency and security analytics.',
    install: 'Linux service with libpcap or as VPC Flow Log subscriber.',
    examples: 'Edge gateway, NAT host, VPN concentrator',
  },
};

export const Agents = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showHelp, setShowHelp] = useState(true);
  const [registeredCreds, setRegisteredCreds] = useState(null); // { agent_id, api_key, name, type }
  const [newAgent, setNewAgent] = useState({
    name: '',
    agent_type: 'kubernetes',
    cluster: '',
    region: '',
    cloud_provider: 'aws',
  });

  useEffect(() => { fetchAgents(); }, []);

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/agent/list`);
      setAgents(response.data.agents);
    } catch (error) {
      console.error('Error fetching agents:', error);
    } finally {
      setLoading(false);
    }
  };

  const registerAgent = async () => {
    try {
      const response = await axios.post(`${API}/agent/register`, newAgent);
      toast.success(`Agent "${newAgent.name}" registered`);
      setRegisterDialogOpen(false);
      setRegisteredCreds({
        agent_id: response.data.agent_id,
        api_key: response.data.api_key,
        name: newAgent.name,
        type: newAgent.agent_type,
      });
      fetchAgents();
      setNewAgent({ name: '', agent_type: 'kubernetes', cluster: '', region: '', cloud_provider: 'aws' });
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to register agent';
      toast.error(typeof detail === 'string' ? detail : 'Failed to register agent');
    }
  };

  const deleteAgent = async () => {
    if (!selectedAgent) return;
    try {
      await axios.delete(`${API}/agent/${selectedAgent.id}`);
      toast.success('Agent deleted');
      setDeleteDialogOpen(false);
      fetchAgents();
    } catch (error) {
      toast.error('Failed to delete agent');
    }
  };

  const formatLastHeartbeat = (timestamp) => {
    if (!timestamp) return 'Never';
    const diff = Date.now() - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const copy = (text, label = 'Copied') => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const activeCount = agents.filter((a) => a.status === 'active').length;
  const totalMetrics = agents.reduce((sum, a) => sum + (a.metrics_collected || 0), 0);
  const selectedTypeInfo = AGENT_TYPE_INFO[newAgent.agent_type];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="agents-page">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold font-['Chivo']">Agents</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Lightweight collectors that run inside your infrastructure (clusters, VMs, cloud APIs)
          and push telemetry — metrics, logs, traces — back to this platform.
        </p>
      </div>

      {/* What is an Agent? — collapsible explainer */}
      <Card className="bg-primary/5 border-primary/20" data-testid="agents-help-card">
        <CardHeader className="pb-2">
          <button
            type="button"
            onClick={() => setShowHelp((v) => !v)}
            className="flex items-center justify-between w-full text-left"
            data-testid="toggle-help"
          >
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-primary" />
              What's an Agent and how do I register one?
            </CardTitle>
            {showHelp ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </button>
        </CardHeader>
        {showHelp && (
          <CardContent className="space-y-4 text-sm">
            <p className="text-muted-foreground leading-relaxed">
              An <span className="text-foreground font-medium">Agent</span> is a small daemon you install
              <em> inside</em> the environment you want to monitor — a Kubernetes cluster, an EC2 instance,
              a VPN gateway, or as a serverless function pulling cloud APIs. It authenticates with an
              API key, sends a heartbeat every ~30 seconds, and streams metrics/logs to this platform.
              Without agents, the platform can only see what the cloud provider's public APIs expose;
              with agents, it sees inside your actual workloads.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(AGENT_TYPE_INFO).map(([key, info]) => {
                const Icon = agentTypeIcons[key] || Server;
                return (
                  <div
                    key={key}
                    className="flex items-start gap-3 p-3 rounded-lg bg-card border border-border"
                    data-testid={`agent-type-info-${key}`}
                  >
                    <div className="p-2 rounded-md bg-primary/10 shrink-0">
                      <Icon className="w-4 h-4 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-foreground">{info.title}</p>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{info.description}</p>
                      <p className="text-xs text-muted-foreground/80 mt-2">
                        <span className="font-medium text-foreground/70">Example targets:</span> {info.examples}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="p-3 rounded-lg bg-card border border-border">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-2">
                Registration flow
                <span className="px-1.5 py-0.5 text-[10px] rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                  v1.1 · available
                </span>
              </p>
              <ol className="text-sm text-muted-foreground space-y-1.5 list-decimal pl-5">
                <li>Click <span className="text-foreground font-medium">Register Agent</span>, fill the form (name, type, cluster, region).</li>
                <li>Save the <span className="text-foreground font-medium">API key</span> shown after registration — it's displayed only once.</li>
                <li>Install the agent on the target host using one of the four install paths below.</li>
                <li>The agent starts sending heartbeats — its row in the table flips to <Badge variant="secondary" className="bg-green-500/15 text-green-500 ml-1">active</Badge>.</li>
              </ol>
            </div>

            {/* ── Install matrix — surfaces the CLI immediately ──────── */}
            <div className="p-4 rounded-lg bg-card border border-border space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Terminal className="w-4 h-4 text-cyan-400" />
                  <p className="text-sm font-semibold text-foreground">CloudGenie Agent · install paths</p>
                  <Badge variant="outline" className="text-[9px] border-cyan-500/40 text-cyan-300">v1.1</Badge>
                </div>
                <p className="text-[10px] text-muted-foreground">
                  Pick the path that matches your target host.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Linux VM / EC2 */}
                <div className="rounded-md border border-border bg-background/30 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Server className="w-3.5 h-3.5 text-emerald-400" />
                    <span className="text-xs font-semibold text-foreground">Linux VM / EC2</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mb-2">One-line bash installer with systemd unit.</p>
                  <pre className="text-[10px] bg-black/40 rounded p-2 overflow-x-auto whitespace-pre">
{'curl -fsSL https://get.cloudgenie.co/install.sh \\\n  | sudo AGENT_ID=… API_KEY=… \\\n         PLATFORM_URL=… bash'}
                  </pre>
                </div>

                {/* Kubernetes */}
                <div className="rounded-md border border-border bg-background/30 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Box className="w-3.5 h-3.5 text-blue-400" />
                    <span className="text-xs font-semibold text-foreground">Kubernetes (Helm)</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mb-2">Drops a Deployment + ClusterRole into your cluster.</p>
                  <pre className="text-[10px] bg-black/40 rounded p-2 overflow-x-auto whitespace-pre">
{'helm repo add cloudgenie https://charts.cloudgenie.co\nhelm install cloudgenie-agent cloudgenie/agent \\\n  --set agentId=… --set apiKey=…'}
                  </pre>
                </div>

                {/* AWS Lambda */}
                <div className="rounded-md border border-border bg-background/30 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Cloud className="w-3.5 h-3.5 text-amber-400" />
                    <span className="text-xs font-semibold text-foreground">AWS Lambda (Terraform)</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mb-2">Serverless collector: EventBridge fires every 15 min.</p>
                  <pre className="text-[10px] bg-black/40 rounded p-2 overflow-x-auto whitespace-pre">
{'module "collector" {\n  source       = "cloudgenie/collector/aws"\n  agent_id     = "…"\n  api_key      = "cga_…"\n  platform_url = "…"\n}'}
                  </pre>
                </div>

                {/* Manual / dev */}
                <div className="rounded-md border border-border bg-background/30 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Terminal className="w-3.5 h-3.5 text-violet-400" />
                    <span className="text-xs font-semibold text-foreground">Manual / development</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mb-2">Build the Go binary from source, run in foreground.</p>
                  <pre className="text-[10px] bg-black/40 rounded p-2 overflow-x-auto whitespace-pre">
{'cd /app/agent-cli && make build\nAGENT_ID=… API_KEY=… \\\n  PLATFORM_URL=… ./bin/cg start'}
                  </pre>
                </div>
              </div>

              <div className="text-[11px] text-muted-foreground leading-relaxed">
                <span className="text-foreground font-medium">Where it lives:</span>{' '}
                Source for all four paths is committed at{' '}
                <code className="px-1 py-0.5 bg-card rounded text-[10px]">/app/agent-cli/</code> (Go CLI) and{' '}
                <code className="px-1 py-0.5 bg-card rounded text-[10px]">/app/deploy/</code> (Helm + bash + Terraform). The
                public download URLs (charts.cloudgenie.co, get.cloudgenie.co, Terraform Registry) are part of the
                v1.1 release rollout owned by the platform team — see{' '}
                <code className="px-1 py-0.5 bg-card rounded text-[10px]">/app/deploy/AGENT_INSTALL.md</code>.
              </div>
              <div className="text-[11px] text-emerald-300/80">
                After registering an agent (button top-right), the dialog shows your agent-specific{' '}
                <code className="px-1 py-0.5 bg-card rounded text-[10px]">AGENT_ID</code> + one-time{' '}
                <code className="px-1 py-0.5 bg-card rounded text-[10px]">API_KEY</code> + a copy-able snippet for each
                install path with those values pre-filled.
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border" data-testid="total-agents">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Agents</p>
                <p className="text-3xl font-bold">{agents.length}</p>
              </div>
              <div className="p-3 rounded-lg bg-primary/10"><Cpu className="w-5 h-5 text-primary" /></div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border" data-testid="active-agents">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-3xl font-bold text-green-500">{activeCount}</p>
              </div>
              <div className="p-3 rounded-lg bg-green-500/10"><Wifi className="w-5 h-5 text-green-500" /></div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border" data-testid="inactive-agents">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Inactive</p>
                <p className="text-3xl font-bold text-yellow-500">{agents.length - activeCount}</p>
              </div>
              <div className="p-3 rounded-lg bg-yellow-500/10"><WifiOff className="w-5 h-5 text-yellow-500" /></div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border" data-testid="total-metrics">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Metrics Collected</p>
                <p className="text-3xl font-bold">{(totalMetrics / 1000).toFixed(0)}K</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-500/10"><Activity className="w-5 h-5 text-blue-500" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Data Collection Agents</h3>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchAgents} data-testid="refresh-agents">
            <RefreshCw className="w-4 h-4 mr-2" />Refresh
          </Button>
          <Button onClick={() => setRegisterDialogOpen(true)} data-testid="register-agent-btn">
            <Plus className="w-4 h-4 mr-2" />Register Agent
          </Button>
        </div>
      </div>

      {/* Agents Table */}
      <Card className="bg-card border-border" data-testid="agents-table">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">Agent</TableHead>
                <TableHead className="text-muted-foreground">Type</TableHead>
                <TableHead className="text-muted-foreground">Cluster</TableHead>
                <TableHead className="text-muted-foreground">Region</TableHead>
                <TableHead className="text-muted-foreground">Status</TableHead>
                <TableHead className="text-muted-foreground">Last Heartbeat</TableHead>
                <TableHead className="text-muted-foreground">Metrics</TableHead>
                <TableHead className="text-muted-foreground">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                    <HardDrive className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <p className="text-sm">No agents registered yet.</p>
                    <p className="text-xs mt-1">
                      Click <span className="text-foreground font-medium">Register Agent</span> above
                      to add your first one.
                    </p>
                  </TableCell>
                </TableRow>
              )}
              {agents.map((agent) => {
                const Icon = agentTypeIcons[agent.agent_type] || Server;
                const status = statusColors[agent.status] || statusColors.inactive;
                return (
                  <TableRow key={agent.id} className="border-border hover:bg-secondary/50" data-testid={`agent-row-${agent.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-secondary"><Icon className="w-4 h-4 text-muted-foreground" /></div>
                        <div>
                          <p className="font-medium">{agent.name}</p>
                          <p className="text-xs text-muted-foreground">v{agent.version}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="capitalize">{agent.agent_type}</TableCell>
                    <TableCell className="font-mono text-sm">{agent.cluster || '-'}</TableCell>
                    <TableCell>{agent.region || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={`${status.bg} ${status.text}`}>
                        <span className={`w-2 h-2 rounded-full ${status.dot} mr-2`} />
                        {agent.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {formatLastHeartbeat(agent.last_heartbeat)}
                      </div>
                    </TableCell>
                    <TableCell>{(agent.metrics_collected || 0).toLocaleString()}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => { setSelectedAgent(agent); setDeleteDialogOpen(true); }}
                        data-testid={`delete-${agent.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Register Agent Dialog */}
      <Dialog open={registerDialogOpen} onOpenChange={setRegisterDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle>Register New Agent</DialogTitle>
            <DialogDescription>
              Creates an entry in the platform and issues an API key. You'll see install instructions after.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="name">Agent Name</Label>
              <Input
                id="name"
                value={newAgent.name}
                onChange={(e) => setNewAgent((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., k8s-prod-agent"
                className="bg-secondary border-border text-foreground"
                data-testid="agent-name-input"
              />
              <p className="text-[11px] text-muted-foreground">
                A friendly identifier — pick something that tells you where it runs.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Agent Type</Label>
              <Select value={newAgent.agent_type} onValueChange={(v) => setNewAgent((prev) => ({ ...prev, agent_type: v }))}>
                <SelectTrigger className="bg-secondary border-border" data-testid="agent-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="kubernetes">Kubernetes</SelectItem>
                  <SelectItem value="vm">VM / EC2</SelectItem>
                  <SelectItem value="cloud">Cloud API</SelectItem>
                  <SelectItem value="network">Network</SelectItem>
                </SelectContent>
              </Select>
              {selectedTypeInfo && (
                <div className="p-3 rounded-md bg-secondary/40 border border-border space-y-1.5">
                  <p className="text-xs text-foreground font-medium flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5 text-primary" />{selectedTypeInfo.title}
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{selectedTypeInfo.description}</p>
                  <p className="text-[11px] text-muted-foreground/80">
                    <span className="text-foreground/80 font-medium">Install:</span> {selectedTypeInfo.install}
                  </p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cluster">Cluster (optional)</Label>
                <Input
                  id="cluster"
                  value={newAgent.cluster}
                  onChange={(e) => setNewAgent((prev) => ({ ...prev, cluster: e.target.value }))}
                  placeholder="prod-eks"
                  className="bg-secondary border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="region">Region</Label>
                <Input
                  id="region"
                  value={newAgent.region}
                  onChange={(e) => setNewAgent((prev) => ({ ...prev, region: e.target.value }))}
                  placeholder="us-east-1"
                  className="bg-secondary border-border text-foreground"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="provider">Cloud Provider</Label>
              <Select value={newAgent.cloud_provider} onValueChange={(v) => setNewAgent((prev) => ({ ...prev, cloud_provider: v }))}>
                <SelectTrigger className="bg-secondary border-border"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="aws">AWS</SelectItem>
                  <SelectItem value="gcp">GCP</SelectItem>
                  <SelectItem value="azure">Azure</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegisterDialogOpen(false)}>Cancel</Button>
            <Button onClick={registerAgent} disabled={!newAgent.name} data-testid="confirm-register">
              <Plus className="w-4 h-4 mr-2" />Register Agent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post-registration: API key + install instructions */}
      <Dialog open={!!registeredCreds} onOpenChange={(v) => !v && setRegisteredCreds(null)}>
        <DialogContent className="bg-card border-border max-w-2xl" data-testid="agent-creds-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              Agent registered: {registeredCreds?.name}
            </DialogTitle>
            <DialogDescription>
              Save the API key now — it won't be shown again. Use the install command on your target host.
            </DialogDescription>
          </DialogHeader>

          {registeredCreds && (
            <div className="space-y-4 py-2">
              {/* Agent ID */}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground uppercase tracking-wide">Agent ID</Label>
                <div className="flex gap-2">
                  <Input value={registeredCreds.agent_id} readOnly className="font-mono text-xs bg-secondary border-border" />
                  <Button size="icon" variant="outline" onClick={() => copy(registeredCreds.agent_id, 'Agent ID copied')}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

              {/* API Key */}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  API Key <span className="text-amber-400 normal-case">— shown only once</span>
                </Label>
                <div className="flex gap-2">
                  <Input value={registeredCreds.api_key} readOnly className="font-mono text-xs bg-secondary border-border" />
                  <Button size="icon" variant="outline" onClick={() => copy(registeredCreds.api_key, 'API key copied')}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

              {/* Install snippet */}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <Terminal className="w-3 h-3" />Install command
                </Label>
                <div className="p-3 rounded-md bg-black/40 border border-border">
                  <pre className="text-[11px] text-emerald-300 font-mono whitespace-pre-wrap leading-relaxed">
{registeredCreds.type === 'kubernetes'
  ? `# Install via Helm
helm repo add cloudgenie https://charts.cloudgenie.co
helm install cloudgenie-agent cloudgenie/agent \\
  --set agentId=${registeredCreds.agent_id} \\
  --set apiKey=${registeredCreds.api_key} \\
  --set platformUrl=$REACT_APP_BACKEND_URL \\
  -n cloudgenie --create-namespace`
  : registeredCreds.type === 'vm'
  ? `# Install on a Linux host (VM/EC2)
curl -fsSL https://get.cloudgenie.co/install.sh | \\
  sudo AGENT_ID=${registeredCreds.agent_id} \\
  API_KEY=${registeredCreds.api_key} bash`
  : registeredCreds.type === 'cloud'
  ? `# Deploy as Lambda via Terraform
module "cloudgenie_collector" {
  source     = "cloudgenie/collector/aws"
  agent_id   = "${registeredCreds.agent_id}"
  api_key    = "${registeredCreds.api_key}"
  platform_url = "$REACT_APP_BACKEND_URL"
}`
  : `# Network agent — systemd service with libpcap
sudo apt install -y cloudgenie-net-agent
echo "AGENT_ID=${registeredCreds.agent_id}" | sudo tee /etc/cloudgenie/agent.env
echo "API_KEY=${registeredCreds.api_key}" | sudo tee -a /etc/cloudgenie/agent.env
sudo systemctl enable --now cloudgenie-net-agent`}
                  </pre>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  After running this on the target host, the agent will start sending heartbeats within ~30 seconds
                  and its row in the table will turn <Badge variant="secondary" className="bg-green-500/15 text-green-500 mx-1 align-middle">active</Badge>.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setRegisteredCreds(null)} data-testid="creds-dialog-done">
              <CheckCircle2 className="w-4 h-4 mr-2" />I've saved the key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Delete Agent</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete agent "{selectedAgent?.name}"? This will stop data collection from this agent.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={deleteAgent} data-testid="confirm-delete">
              <Trash2 className="w-4 h-4 mr-2" />Delete Agent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Agents;
